import { useState, useEffect } from "react";
import { useApp } from "@/contexts/AppContext";
import api, { formatCurrency } from "@/lib/api";
import { Play, Download, AlertTriangle, CheckCircle } from "lucide-react";

export default function DepreciationPage() {
  const { activeEntity, getCategoryName } = useApp();
  const [schedule, setSchedule] = useState([]);
  const [totalCharge, setTotalCharge] = useState(0);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [lastJournal, setLastJournal] = useState(null);
  const [period, setPeriod] = useState(new Date().toISOString().substring(0, 7));

  useEffect(() => {
    if (!activeEntity) return;
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/depreciation/schedule", { params: { entity_id: activeEntity.id } });
        setSchedule(data.schedule);
        setTotalCharge(data.total_monthly_charge);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [activeEntity]);

  const runDepreciation = async () => {
    if (!activeEntity) return;
    if (!window.confirm(`Run monthly depreciation for ${activeEntity.name} period ${period}?\n\nTotal charge: ${formatCurrency(totalCharge)}`)) return;
    setRunning(true);
    try {
      const { data } = await api.post("/depreciation/run", { entity_id: activeEntity.id, period });
      setLastJournal(data.journal);
      alert(`Depreciation posted!\nReference: ${data.journal.reference}\nTotal: ${formatCurrency(data.total_charge)}\nCategories: ${data.categories_affected}`);
      // Reload schedule
      const { data: d2 } = await api.get("/depreciation/schedule", { params: { entity_id: activeEntity.id } });
      setSchedule(d2.schedule);
      setTotalCharge(d2.total_monthly_charge);
    } catch (e) {
      alert(e.response?.data?.detail || "Error running depreciation");
    } finally {
      setRunning(false);
    }
  };

  if (!activeEntity) {
    return (
      <div className="flex items-center justify-center h-64 text-[#6B7280]" data-testid="depreciation-page">
        <p>Select an entity from the sidebar to run depreciation.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="depreciation-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-[#0B1020]">Depreciation</h1>
          <p className="text-sm text-[#6B7280] mt-1">{activeEntity.name} - Monthly depreciation schedule</p>
        </div>
        <div className="flex items-center gap-3">
          <input type="month" className="stratum-input" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="depreciation-period-input" />
          <button onClick={runDepreciation} disabled={running || totalCharge === 0} className="stratum-btn-primary flex items-center gap-2 disabled:opacity-50" data-testid="run-depreciation-btn">
            <Play size={16} /> {running ? "Running..." : "Run Monthly Depreciation"}
          </button>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="stratum-card p-5">
          <span className="text-xs text-[#6B7280] uppercase tracking-wider">Next Monthly Charge</span>
          <div className="font-mono text-2xl font-semibold text-[#0B1020] tabular-nums mt-2">{formatCurrency(totalCharge)}</div>
        </div>
        <div className="stratum-card p-5">
          <span className="text-xs text-[#6B7280] uppercase tracking-wider">Annual Equivalent</span>
          <div className="font-mono text-2xl font-semibold text-[#C8A961] tabular-nums mt-2">{formatCurrency(totalCharge * 12)}</div>
        </div>
        <div className="stratum-card p-5">
          <span className="text-xs text-[#6B7280] uppercase tracking-wider">Assets to Depreciate</span>
          <div className="font-mono text-2xl font-semibold text-[#0B1020] tabular-nums mt-2">{schedule.filter(s => s.monthly_charge > 0).length}</div>
        </div>
        <div className="stratum-card p-5">
          <span className="text-xs text-[#6B7280] uppercase tracking-wider">Period</span>
          <div className="font-mono text-2xl font-semibold text-[#0B1020] tabular-nums mt-2">{period}</div>
        </div>
      </div>

      {/* Last Journal */}
      {lastJournal && (
        <div className="stratum-card p-5 bg-[#065F46]/5 border-[#065F46]/20">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle size={18} className="text-[#065F46]" />
            <span className="font-medium text-[#065F46]">Journal Posted: {lastJournal.reference}</span>
          </div>
          <div className="space-y-1">
            {lastJournal.lines?.map((line, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span className="text-[#1A1A1A]">{line.account}</span>
                <div className="flex gap-8">
                  <span className="font-mono tabular-nums w-28 text-right">{line.debit > 0 ? formatCurrency(line.debit) : ""}</span>
                  <span className="font-mono tabular-nums w-28 text-right">{line.credit > 0 ? formatCurrency(line.credit) : ""}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Schedule Table */}
      <div className="stratum-card overflow-hidden">
        <table className="w-full stratum-table" data-testid="depreciation-table">
          <thead>
            <tr>
              <th>Tag</th>
              <th>Description</th>
              <th>Category</th>
              <th className="text-right">Cost</th>
              <th className="text-right">Accum Dep</th>
              <th className="text-right">Carrying</th>
              <th className="text-right">Monthly Charge</th>
              <th className="text-right">Annual Charge</th>
              <th className="text-right">Remaining Life</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="text-center py-8 text-[#6B7280]">Loading...</td></tr>
            ) : schedule.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-8 text-[#6B7280]">No assets in this entity</td></tr>
            ) : schedule.map((s) => (
              <tr key={s.asset_id} data-testid={`dep-row-${s.tag}`}>
                <td className="font-mono text-sm font-medium">{s.tag}</td>
                <td className="max-w-[180px] truncate text-sm">{s.description}</td>
                <td className="text-xs">{getCategoryName(s.category_id)}</td>
                <td className="text-right font-mono tabular-nums text-sm">{formatCurrency(s.cost)}</td>
                <td className="text-right font-mono tabular-nums text-sm text-[#991B1B]">({formatCurrency(Math.abs(s.accumulated_depreciation))})</td>
                <td className="text-right font-mono tabular-nums text-sm font-medium">{formatCurrency(s.carrying_amount)}</td>
                <td className="text-right font-mono tabular-nums text-sm font-medium text-[#C8A961]">{formatCurrency(s.monthly_charge)}</td>
                <td className="text-right font-mono tabular-nums text-sm font-medium">{formatCurrency(s.annual_charge || s.monthly_charge * 12)}</td>
                <td className="text-right font-mono tabular-nums text-sm text-[#6B7280]">{s.remaining_life_months} mo</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
